/**
 * Trial Balance Report (BE-30)
 *
 * Every GL account with its opening balance, the range's debits and credits and its closing balance (Dr / Cr), with
 * totals that must balance. The range stays within one calendar year (P&L accounts restart on 1 January). Clicking an
 * account opens it in the General Ledger report (BE-33) for the same dates.
 * Route: /reports/trial-balance
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Alert, Chip, Skeleton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { CurrencyText, ReportExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { trialBalanceService } from '../../services/accounting/trialBalance.service';
import { serverMessage } from '../../utils/apiConvert';

const Amount = ({ value, bold }) => (value == null || Number(value) === 0
  ? <Typography component="span" sx={{ fontSize: fs.sm, color: 'text.disabled' }}>—</Typography>
  : <CurrencyText amount={value} size="sm" bold={bold} withPrefix={false} />);

const NUM = { align: 'right', sx: { whiteSpace: 'nowrap' } };

const TrialBalance = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const today = toYmd(new Date());
  const [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sameYear = from && to && from.slice(0, 4) === to.slice(0, 4) && from <= to;

  const load = useCallback(async () => {
    if (!sameYear) return;
    setLoading(true);
    setError(null);
    try {
      setData(await trialBalanceService.get({ from, to }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the trial balance'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, sameYear]);

  useEffect(() => { load(); }, [load]);

  const head = { fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', py: 0.75 };
  const cell = { fontSize: fs.sm, py: 0.5 };
  const totals = data?.totals;

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Trial Balance</Typography>
          {data && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
              {formatDate(data.from)} to {formatDate(data.to)} · posted entries · P&L accounts from 1 January
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField label="From" type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <TextField label="To" type="date" size="small" value={to} onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <ReportExportMenu reportType="trial-balance" params={{ from, to }} onError={setError} />
        </Box>
      </Box>

      {!sameYear && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Pick a From and To date in the same calendar year, From first.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {data?.notes?.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {data.notes.map((n) => <Typography key={n} sx={{ fontSize: '0.75rem' }}>{n}</Typography>)}
        </Alert>
      )}
      {loading && sameYear ? (
        <Box>{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} height={28} sx={{ mt: 1 }} />)}</Box>
      ) : !data ? (
        !error && sameYear && <Alert severity="info">No trial balance available for this range.</Alert>
      ) : data.rows.length === 0 ? (
        <Alert severity="info">Nothing is posted up to {formatDate(data.to)}.</Alert>
      ) : (
        <TableContainer sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Account</TableCell>
                <TableCell sx={head} align="right" colSpan={2}>Opening</TableCell>
                <TableCell sx={head} align="right">Debit</TableCell>
                <TableCell sx={head} align="right">Credit</TableCell>
                <TableCell sx={head} align="right" colSpan={2}>Closing</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...head, top: 33 }} />
                <TableCell sx={{ ...head, top: 33 }} align="right">Dr</TableCell>
                <TableCell sx={{ ...head, top: 33 }} align="right">Cr</TableCell>
                <TableCell sx={{ ...head, top: 33 }} />
                <TableCell sx={{ ...head, top: 33 }} />
                <TableCell sx={{ ...head, top: 33 }} align="right">Dr</TableCell>
                <TableCell sx={{ ...head, top: 33 }} align="right">Cr</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.accountNumber} hover sx={{ cursor: 'pointer' }} title="Open this account in the General Ledger"
                  onClick={() => navigate(`/reports/general-ledger?account=${encodeURIComponent(r.accountNumber)}&from=${data.from}&to=${data.to}`)}>
                  <TableCell sx={cell}>
                    <Box component="span" sx={{ color: 'text.secondary', mr: 1, fontVariantNumeric: 'tabular-nums' }}>{r.accountNumber}</Box>
                    {r.accountName}
                  </TableCell>
                  <TableCell {...NUM}><Amount value={r.opening?.debit} /></TableCell>
                  <TableCell {...NUM}><Amount value={r.opening?.credit} /></TableCell>
                  <TableCell {...NUM}><Amount value={r.periodDebit} /></TableCell>
                  <TableCell {...NUM}><Amount value={r.periodCredit} /></TableCell>
                  <TableCell {...NUM}><Amount value={r.closing?.debit} /></TableCell>
                  <TableCell {...NUM}><Amount value={r.closing?.credit} /></TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                <TableCell sx={{ ...cell, fontWeight: fw.bold }}>Total</TableCell>
                <TableCell {...NUM}><Amount value={totals.openingDebit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={totals.openingCredit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={totals.periodDebit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={totals.periodCredit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={totals.closingDebit} bold /></TableCell>
                <TableCell {...NUM}><Amount value={totals.closingCredit} bold /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              label={data.balanced ? 'Balanced' : `NOT Balanced — difference ${Number(data.difference || 0).toFixed(2)}`}
              size="small"
              sx={{
                bgcolor: data.balanced ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: data.balanced ? '#22c55e' : '#ef4444',
                fontWeight: fw.semibold,
                fontSize: fs.xs,
              }}
            />
          </Box>
        </TableContainer>
      )}
    </Box>
  );
};

export default TrialBalance;
